import React, { useEffect, useRef, useState, useCallback } from 'react';
import styles from './Heap.module.css';
import HeapNode from '../HeapNode/HeapNode';
import { HeapNode as HeapNodeType, HeapProps } from '../../types';

const Heap: React.FC<HeapProps> = ({ 
  nodes, 
  outlineNodes, 
  gameMode, 
  onNodeClick, 
  onOutlineClick 
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  // Define SVG marker for arrows outside of the render loop
  const arrowMarkerId = "heap-arrow-marker";
  
  // Calculate base node size based on screen dimensions - available throughout component
  const baseNodeSize = 64; // Base size of node in pixels for desktop
  
  // Device detection for optimal layout
  const isDesktop = containerDimensions.width > 768;
  const isTablet = containerDimensions.width <= 768 && containerDimensions.width > 480;
  const isPhone = containerDimensions.width <= 480;
  const isSmallPhone = containerDimensions.width <= 350;
  
  // Calculate the responsive node size
  const nodeSize = isSmallPhone 
    ? Math.min(baseNodeSize * 0.4, containerDimensions.width / 10) // 40% size on small phones
    : isPhone 
      ? Math.min(baseNodeSize * 0.6, containerDimensions.width / 10) // 60% size on regular phones
      : isTablet 
        ? Math.min(baseNodeSize * 0.8, containerDimensions.width / 10) // 80% size on tablets
        : baseNodeSize; // Full size on desktop
  
  // Calculate node positions based on heap structure and handle window resize
  useEffect(() => {
    if (containerRef.current) {
      const updateDimensions = () => {
        if (containerRef.current) {
          const { width, height } = containerRef.current.getBoundingClientRect();
          setContainerDimensions({ width, height });
        }
      };

      // Initial size
      updateDimensions();
      
      // Update on window resize
      window.addEventListener('resize', updateDimensions);
      
      return () => {
        window.removeEventListener('resize', updateDimensions);
      };
    }
  }, []);
  
  // Reposition nodes when nodes change or dimensions change
  useEffect(() => {
    if (containerRef.current) {
      // Redraw node positions
      const nodeElements = containerRef.current.querySelectorAll(`.${styles.nodeWrapper}`);
      
      nodeElements.forEach(nodeElem => {
        const position = parseInt(nodeElem.querySelector('[data-position]')?.getAttribute('data-position') || '0');
        const node = nodes.find(n => n.position === position);
        
        if (node) {
          const { left, top } = getNodePosition(position);
          (nodeElem as HTMLElement).style.left = `${left}px`;
          (nodeElem as HTMLElement).style.top = `${top}px`;
        }
      });
      
      // Lines are now drawn with SVG and will automatically update with React
    }
  }, [nodes, containerDimensions]);

  // Add an effect to force re-render on window resize with debouncing
  useEffect(() => {
    let resizeTimer: NodeJS.Timeout;
    
    const handleResize = () => {
      // Clear previous timer
      clearTimeout(resizeTimer);
      
      // Set a new timer to debounce the resize event
      resizeTimer = setTimeout(() => {
        console.log('Window resized, updating dimensions');
        // Force a re-render by updating container dimensions
        if (containerRef.current) {
          const { width, height } = containerRef.current.getBoundingClientRect();
          setContainerDimensions({ width, height });
        }
      }, 100); // 100ms debounce time
    };

    // Add resize event listener
    window.addEventListener('resize', handleResize);
    
    // Initial measurement
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setContainerDimensions({ width, height });
    }
    
    // Clean up
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Calculate node position in the heap visualization
  const getNodePosition = (position: number) => {
    if (!containerDimensions.width || !containerDimensions.height) return { left: 0, top: 0 };
    
    // Calculate tree level and position within level
    // For a binary heap with 0-based indexing:
    // Level 0: Node 0 (root)
    // Level 1: Nodes 1-2
    // Level 2: Nodes 3-6
    // Level 3: Nodes 7-14
    const level = Math.floor(Math.log2(position + 1));
    const nodesInLevel = Math.pow(2, level);
    const positionInLevel = position - (Math.pow(2, level) - 1);
    
    // We're now using the top-level nodeSize variable instead of redefining it here
    
    // Calculate base horizontal spacing as a percentage of container width
    // This allows it to scale more aggressively as width decreases
    const baseHorizontalSpacing = Math.min(
      nodeSize * 1.0, // Cap at original node size
      Math.max(containerDimensions.width * 0.05, 20) // Min 20px, otherwise 5% of width
    );
    
    // Further increase horizontal spacing between nodes
    // Using more width to provide adequate separation between smaller nodes
    const minHorizontalSpacing = Math.min(
      baseHorizontalSpacing,
      containerDimensions.width / (
        isSmallPhone ? 6 : // Moderate compression for small phones
        isPhone ? 5 : // Moderate compression for regular phones
        isTablet ? 8 : // Less aggressive on tablets
        10 // Original desktop spacing
      )
    );
    
    // Adjust top margin based on screen size
    // Reduce top margin on mobile to give more vertical space
    const topMargin = isPhone || isTablet
      ? containerDimensions.height * 0.115 // Reduced margin on mobile for more vertical space
      : containerDimensions.height * 0.15; // Original desktop margin
    
    // Adjust vertical spacing based on device type
    // Increase spacing on phones to prevent row overlap
    const levelSpacing = isPhone || isSmallPhone
      ? [
          90,  // Spacing after level 0 (root) - increased for phones
          110, // Spacing after level 1 - increased for phones
          130, // Spacing after level 2 - increased for phones
          150  // Spacing after level 3 - increased for phones
        ]
      : [
          80,  // Spacing after level 0 (root) - original desktop spacing
          100, // Spacing after level 1 - original desktop spacing
          120, // Spacing after level 2 - original desktop spacing
          140  // Spacing after level 3 - original desktop spacing
        ];
    
    // Calculate vertical position based on fixed level spacing
    let top = topMargin;
    for (let i = 0; i < level; i++) {
      top += (i < levelSpacing.length) ? levelSpacing[i] : 120;
    }
    
    // Calculate horizontal position
    // Important: Each node must be centered above its children
    // For a binary heap: parent at index i has children at 2i+1 and 2i+2
    
    // First, determine the full width needed for the deepest visible level
    const maxLevel = 4; // Maximum levels to support
    const deepestLevelNodes = Math.pow(2, maxLevel);
    
    // Use consistent horizontal padding to ensure proper centering
    // For phones, ensure equal padding on both sides with smaller nodes
    const horizontalPadding = isDesktop
      ? nodeSize // Original desktop padding
      : (isSmallPhone || isPhone) 
          // Fixed padding for phones - make it consistent from both sides for proper centering
          ? containerDimensions.width * 0.06 // 6% of width as padding on both sides (smaller with smaller nodes)
          : Math.max(containerDimensions.width * 0.05, nodeSize * 0.6); // Tablet padding
    
    const availableWidth = containerDimensions.width - (horizontalPadding * 2);
    
    // Each node at the deepest level gets equal space
    const unitWidth = Math.max(availableWidth / deepestLevelNodes, minHorizontalSpacing);
    
    // Calculate the section width for this level
    // Higher levels get proportionally more space to stay centered above children
    const sectionWidth = unitWidth * Math.pow(2, maxLevel - level);
    
    // Calculate horizontal position within level
    // Subtract half node size to center the node on this position
    const left = horizontalPadding + (sectionWidth * positionInLevel) + (sectionWidth / 2) - (nodeSize / 2);
    
    return { left, top };
  };
  
  // All nodes are now in a single array in the nodes prop
  // outlineNodes prop is kept for backward compatibility but not used
  
  // Create SVG connection lines - we need to recalculate on every render to catch all node changes
  const createConnectionLines = () => {
    // Only create connections if we have nodes
    if (nodes.length <= 1) return null;
    
    const connections = [];
    
    // Add a pixelated arrowhead marker definition
    connections.push(
      <defs key="arrow-marker-defs">
        <marker
          id={arrowMarkerId}
          viewBox="0 0 16 16"
          refX="7"  // Position closer to the line
          refY="7"
          markerWidth="10"
          markerHeight="10"
          orient="auto"
        >
          {/* Symmetric arrow with brighter color and better spacing */}
          <g>
            {/* First point (top) */}
            <rect x="2" y="2" width="2" height="2" fill="#ff2d8a" />
            
            {/* Second point (middle-top) */}
            <rect x="4" y="4" width="2" height="2" fill="#ff2d8a" />
            
            {/* Third point (center) */}
            <rect x="6" y="6" width="2" height="2" fill="#ff2d8a" />
            
            {/* Fourth point (middle-bottom) */}
            <rect x="4" y="8" width="2" height="2" fill="#ff2d8a" />
            
            {/* Fifth point (bottom) */}
            <rect x="2" y="10" width="2" height="2" fill="#ff2d8a" />
          </g>
        </marker>
      </defs>
    );
    
    for (const node of nodes) {
      if (node.position === 0) continue; // Root has no parent
      
      const parentPosition = Math.floor((node.position - 1) / 2);
      
      // For outline nodes, make sure the parent is a real node that exists
      if (node.isOutline) {
        const parentNode = nodes.find(n => n.position === parentPosition && !n.isOutline);
        if (!parentNode) continue; // Skip if parent doesn't exist
      }
      
      // Get the calculated positions
      const childPos = getNodePosition(node.position);
      const parentPos = getNodePosition(parentPosition);
      
      // Create a unique key for this connection
      const key = `connection-${node.position}-${parentPosition}`;
      
      // Check if this is an animating connection
      const childNode = node;
      const parentNode = nodes.find(n => n.position === parentPosition);
      
      // Determine if this is an animating line
      const isAnimating = childNode.isSwapping || childNode.isBubbleUp || parentNode?.isBubbleDown;
      
      // Determine if we should show an arrow for this connection
      let showArrow = false;
      let arrowPointsUp = false;
      
      // Only show arrow if BOTH nodes in a connection need swapping
      // This ensures we only show one arrow for the actual swap that needs to happen
      if (childNode.needsSwap && parentNode?.needsSwap) {
        showArrow = true;
        
        // Direction needs to be reversed - the arrow should point FROM the clicked node TO its swap destination
        if (gameMode === 'min-heap') {
          // In min-heap, arrow points from larger to smaller value
          arrowPointsUp = childNode.value < parentNode.value;
        } else {
          // In max-heap, arrow points from smaller to larger value
          arrowPointsUp = childNode.value > parentNode.value;
        }
        
        // Flip the direction since we want to show destination
        arrowPointsUp = !arrowPointsUp;
      }
      
      // Show arrows for nodes that are currently animating a swap
      if (childNode.isBubbleUp || parentNode?.isBubbleDown) {
        showArrow = true;
        arrowPointsUp = childNode.isBubbleUp || false; // If bubble up, arrow points up
      }
      
      // If no need for arrow, just use a simple line
      if (!showArrow) {
        connections.push(
          <line 
            key={key}
            x1={parentPos.left + nodeSize/2} // Center of the node
            y1={parentPos.top + nodeSize/2}
            x2={childPos.left + nodeSize/2}
            y2={childPos.top + nodeSize/2}
            className={`${styles.nodeLine} ${isAnimating ? styles.animatingLine : ''} ${node.isOutline ? styles.outlineLine : ''}`}
          />
        );
      } else {
        // Calculate the direction vector between the nodes
        const dx = childPos.left - parentPos.left;
        const dy = childPos.top - parentPos.top;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Create a unit vector for direction
        const unitX = dx / distance;
        const unitY = dy / distance;
        
        // Node radius plus padding based on current node size
        const nodeCenter = nodeSize / 2;
        const nodeRadius = nodeSize * 0.56; // Approximately 36px when nodeSize is 64px
        const arrowPadding = nodeSize * 0.125; // Approximately 8px when nodeSize is 64px
        
        // Determine the source and target points with padding from node centers
        const sourceX = arrowPointsUp 
          ? parentPos.left + nodeCenter + (unitX * nodeRadius) 
          : childPos.left + nodeCenter - (unitX * nodeRadius);
        const sourceY = arrowPointsUp 
          ? parentPos.top + nodeCenter + (unitY * nodeRadius) 
          : childPos.top + nodeCenter - (unitY * nodeRadius);
        
        // For the target point, add extra space for the arrow to be visible
        // We don't go all the way to the node edge
        const targetX = arrowPointsUp 
          ? childPos.left + nodeCenter - (unitX * (nodeRadius + arrowPadding)) 
          : parentPos.left + nodeCenter + (unitX * (nodeRadius + arrowPadding));
        const targetY = arrowPointsUp 
          ? childPos.top + nodeCenter - (unitY * (nodeRadius + arrowPadding)) 
          : parentPos.top + nodeCenter + (unitY * (nodeRadius + arrowPadding));
        
        // Add the arrow line with the shared marker
        connections.push(
          <line 
            key={key}
            x1={sourceX}
            y1={sourceY}
            x2={targetX}
            y2={targetY}
            className={`${styles.nodeLine} ${styles.arrowLine} ${isAnimating ? styles.animatingLine : ''} ${node.isOutline ? styles.outlineLine : ''}`}
            markerEnd={`url(#${arrowMarkerId})`}
            strokeWidth="4"
          />
        );
      }
    }
    
    return connections;
  };

  return (
    <div className={styles.heapContainer} ref={containerRef}>
      {/* SVG layer for connection lines */}
      <svg 
        className={styles.connectionsSvg} 
        width="100%" 
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
      >
        {createConnectionLines()}
      </svg>
      
      {nodes.map(node => {
        // Use the node's own isOutline property
        const isOutline = !!node.isOutline;
        const { left, top } = getNodePosition(node.position);
        
        return (
          <div 
            key={node.id} 
            className={styles.nodeWrapper}
            style={{ 
              left: `${left}px`, 
              top: `${top}px`,
              transform: 'none' // We're using CSS animations instead for better performance
            }}
          >
            <HeapNode 
              id={node.id}
              value={node.value} 
              position={node.position}
              isRoot={node.position === 0}
              isMinHeap={gameMode === 'min-heap'}
              isOutline={isOutline}
              isSwapping={node.isSwapping}
              isBubbleUp={node.isBubbleUp}
              isBubbleDown={node.isBubbleDown}
              needsSwap={node.needsSwap}
              size={nodeSize} // Pass the calculated node size
              onClick={isOutline ? onOutlineClick : onNodeClick}
            />
          </div>
        );
      })}
    </div>
  );
};

export default Heap;
